import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { CompanyAvatar } from "@/components/StatusBadge";
import { BOLAG, formatSEK } from "@/lib/portfolio-data";
import {
  addSvar,
  getInstallningar,
  getMottagare,
  getSvar,
  inkubationsBolag,
  markSkickat,
  nastaUtskick,
  regenerateToken,
  setInstallningar,
  updateMottagare,
  type RapportMottagare,
  type RapportSvar,
} from "@/lib/reports-store";
import { Copy, Link2, Mail, RefreshCw, Send, FileText, Settings2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/rapporter")({
  head: () => ({
    meta: [
      { title: "Rapporter — Inkubera" },
      { name: "description", content: "Administrera kvartalsrapporter från inkubationsbolag." },
    ],
  }),
  component: Rapporter,
});

function Rapporter() {
  const [mottagare, setMottagare] = useState<RapportMottagare[]>([]);
  const [svar, setSvarState] = useState<RapportSvar[]>([]);
  const [intervall, setIntervall] = useState(2);
  const [aktiv, setAktiv] = useState(true);
  const [tab, setTab] = useState<"mottagare" | "svar">("mottagare");

  useEffect(() => {
    setMottagare(getMottagare());
    setSvarState(getSvar());
    const inst = getInstallningar();
    setIntervall(inst.intervallManader);
    setAktiv(inst.aktiv);
  }, []);

  const bolag = useMemo(() => inkubationsBolag(), []);
  const bolagMap = useMemo(() => new Map(BOLAG.map((b) => [b.id, b])), []);

  const sparaInstallningar = (next: { intervallManader?: number; aktiv?: boolean }) => {
    const v = setInstallningar({ intervallManader: next.intervallManader ?? intervall, aktiv: next.aktiv ?? aktiv });
    setIntervall(v.intervallManader);
    setAktiv(v.aktiv);
  };

  const onEmail = (id: string, email: string) => {
    setMottagare(updateMottagare(id, { email }));
  };
  const onRegen = (id: string) => {
    setMottagare(regenerateToken(id));
    toast.success("Ny länk genererad");
  };
  const onMarkSent = (id: string) => {
    setMottagare(markSkickat(id));
    toast.success("Markerat som skickat");
  };
  const onCopy = async (token: string) => {
    const url = `${window.location.origin}/rapport/${token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Länk kopierad");
  };

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-7xl">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Inkubationsrapportering
            </div>
            <h1 className="font-display text-4xl">Rapporter</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Skicka ett standardiserat formulär till inkubationsbolag varannan månad. Svaren
              landar automatiskt här i plattformen.
            </p>
          </div>
        </div>

        {/* Inställningar */}
        <div className="rounded-2xl border border-border bg-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="size-4 text-muted-foreground" />
            <h2 className="font-display text-lg">Schema för automatiska utskick</h2>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3">
              <span className="text-sm">Intervall</span>
              <select
                value={intervall}
                onChange={(e) => sparaInstallningar({ intervallManader: Number(e.target.value) })}
                className="h-9 rounded-lg bg-secondary px-3 text-sm border border-transparent focus:border-ring focus:outline-none"
              >
                {[1, 2, 3, 6].map((n) => (
                  <option key={n} value={n}>
                    Var {n}:e månad
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={aktiv}
                onChange={(e) => sparaInstallningar({ aktiv: e.target.checked })}
                className="size-4"
              />
              Automatiskt utskick aktivt
            </label>
            <div className="text-xs text-muted-foreground">
              När backend aktiveras körs utskick enligt detta schema. Just nu kan du skicka manuellt.
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4">
          {(["mottagare", "svar"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 h-9 rounded-lg text-sm transition ${
                tab === t ? "bg-foreground text-background" : "bg-secondary text-foreground/70 hover:text-foreground"
              }`}
            >
              {t === "mottagare" ? `Mottagare (${mottagare.length})` : `Inkomna svar (${svar.length})`}
            </button>
          ))}
        </div>

        {tab === "mottagare" && (
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Bolag</th>
                  <th className="px-4 py-3 font-medium">E-post</th>
                  <th className="px-4 py-3 font-medium">Senast skickat</th>
                  <th className="px-4 py-3 font-medium">Nästa utskick</th>
                  <th className="px-4 py-3 font-medium text-right">Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {mottagare.map((m) => {
                  const b = bolagMap.get(m.bolagId);
                  if (!b) return null;
                  const nasta = nastaUtskick(m.senastSkickat, intervall);
                  return (
                    <tr key={m.bolagId} className="border-t border-border">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <CompanyAvatar initialer={b.namn.slice(0, 2).toUpperCase()} farg="oklch(0.7 0.15 250)" />
                          <div>
                            <div className="font-medium">{b.namn}</div>
                            <div className="text-xs text-muted-foreground">{b.branscher?.[0]}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="size-3.5 text-muted-foreground" />
                          <input
                            value={m.email}
                            onChange={(e) => onEmail(m.bolagId, e.target.value)}
                            placeholder="vd@bolag.se"
                            className="h-8 w-56 rounded-md bg-secondary px-2 text-sm border border-transparent focus:border-ring focus:outline-none"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {m.senastSkickat ? new Date(m.senastSkickat).toLocaleDateString("sv-SE") : "—"}
                      </td>
                      <td className="px-4 py-3">{nasta.toLocaleDateString("sv-SE")}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onCopy(m.token)}
                            className="h-8 px-2.5 rounded-md hover:bg-secondary inline-flex items-center gap-1.5 text-xs"
                            title="Kopiera länk"
                          >
                            <Link2 className="size-3.5" /> Kopiera länk
                          </button>
                          <button
                            onClick={() => onRegen(m.bolagId)}
                            className="h-8 px-2.5 rounded-md hover:bg-secondary inline-flex items-center gap-1.5 text-xs"
                            title="Ny token"
                          >
                            <RefreshCw className="size-3.5" />
                          </button>
                          <button
                            onClick={() => onMarkSent(m.bolagId)}
                            disabled={!m.email}
                            className="h-8 px-2.5 rounded-md bg-foreground text-background inline-flex items-center gap-1.5 text-xs disabled:opacity-40"
                          >
                            <Send className="size-3.5" /> Markera skickat
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {mottagare.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      Inga inkubationsbolag hittades.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "svar" && (
          <div className="space-y-3">
            {svar.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
                Inga svar har kommit in ännu. Testa formuläret via en länk i fliken Mottagare.
              </div>
            )}
            {svar.map((s) => {
              const total = s.finansiellaStod + s.krediter + s.agarkapitalEmissioner + s.agarkapitalKonvertibler;
              return (
                <div key={s.id} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <FileText className="size-4 text-muted-foreground" />
                      <div>
                        <div className="font-display text-lg">{s.bolagNamn}</div>
                        <div className="text-xs text-muted-foreground">
                          Inskickat {new Date(s.inskickat).toLocaleString("sv-SE")}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Totalt kapital</div>
                      <div className="font-display text-xl">{formatSEK(total)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                    <Stat label="Personer" value={s.antalPersoner.toString()} />
                    <Stat label="Aktieägare" value={s.antalAktieagare.toString()} />
                    <Stat label="Finansiella stöd" value={formatSEK(s.finansiellaStod)} />
                    <Stat label="Krediter" value={formatSEK(s.krediter)} />
                    <Stat label="Emissioner" value={formatSEK(s.agarkapitalEmissioner)} />
                    <Stat label="Konvertibler" value={formatSEK(s.agarkapitalKonvertibler)} />
                  </div>
                  {s.kommentar && (
                    <div className="mt-4 text-sm text-muted-foreground border-t border-border pt-3">
                      {s.kommentar}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}

// re-export so route file isn't tree-shaken away accidentally
export { addSvar };
